// ─────────────────────────────────────────────────────────────────────────────
// Consent-gated Google Analytics
//
// gtag.js is NOT loaded until the visitor explicitly grants analytics consent
// (via the cookie banner) or has previously stored that consent. This keeps the
// site free of tracking cookies/scripts by default, in line with the Privacy &
// GDPR page.
// ─────────────────────────────────────────────────────────────────────────────

export const GA_TRACKING_ID = "G-4MWVG7D2MK";
export const CONSENT_KEY = "cookie-consent";

export interface CookieConsent {
  necessary: boolean;
  analytics: boolean;
}

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
  }
}

let gaInitialised = false;

export const getStoredConsent = (): CookieConsent | null => {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(CONSENT_KEY);
    return raw ? (JSON.parse(raw) as CookieConsent) : null;
  } catch {
    return null;
  }
};

export const hasAnalyticsConsent = (): boolean =>
    getStoredConsent()?.analytics === true;

/**
 * Inject gtag.js and configure GA. Safe to call multiple times - it only runs
 * once, and only when analytics consent is present.
 */
export const loadAnalytics = (): void => {
  if (gaInitialised || typeof window === "undefined") return;
  if (!hasAnalyticsConsent()) return;
  gaInitialised = true;

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_TRACKING_ID}`;
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer.push(arguments);
  };
  window.gtag("js", new Date());
  // Page views are sent explicitly per route (see trackPageView) so that
  // client-side navigations are counted exactly once.
  window.gtag("config", GA_TRACKING_ID, {
    anonymize_ip: true,
    send_page_view: false,
  });
};

/** Record a page view. No-op until analytics has been loaded with consent. */
export const trackPageView = (path: string): void => {
  if (!gaInitialised || typeof window === "undefined" || !window.gtag) return;
  // Re-check stored consent on every call: if the visitor revokes analytics
  // after GA was loaded, stop sending events immediately.
  if (!hasAnalyticsConsent()) return;
  window.gtag("event", "page_view", {
    page_path: path,
    page_location: window.location.href,
    page_title: document.title,
  });
};
